// Copyright (c) Microsoft Corporation. All rights reserved.
import { getParser } from 'bowser';
import { logitechAppId, razerAppId } from '../constants';
import { DeviceCapabilities } from '../DeviceCapabilities';
import { Device, Trilean } from '../typings';
import { fireTvUserAgentRegex, getTizenAppBrowserVersion, getTizenSystemInformation, isTvAppUserAgent, isTvUserAgent, } from '../utils';
import { castAsError } from '../utils/castAsError';
// Finding the difference between iPhone and iPads is a matter of difference in
// aspect ratios iPhones are taller and thinner, iPad more closely resemble a
// box analyzing all the current aspect ratios out there, the magic number is
// 0.61 that separates the 2 form factors
const IPHONE_IPAD_DIVIDE = 0.61;
const isClient = typeof window !== 'undefined';
const fbMessengerUserAgentRegex = /\bFB[\w_]+\/(Messenger|MESSENGER)/;
const fbAppUserAgentRegex = /\bFB[\w_]+\/(iOS|iPhone|phone|FB)/;
const twitterAppUserAgentRegex = /\bTwitter\b/;
const instagramUserAgentRegex = /\bInstagram\b/;
// Mozilla/5.0 (Linux; Android 12; Razer Edge WiFi Build/SKQ1.211103.001; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/105.0.5195.136 Mobile Safari/537.36
const razerUserAgentRegex = /Android\s\d+;\sRazer\s[0-9a-zA-Z ]+\sBuild/;
// Mozilla/5.0 (Linux; Android 11; GR0006) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/106.0.0.0 Mobile Safari/537.36
const logitechGRRegex = /Linux;\sAndroid\s\d+;\sGR\d{4}\b/;
// Crawlers
const googleBotRegex = /Googlebot\//;
const bingBotRegex = /bingbot/;
const UNKNOWN = 'unknown';
/**
 * Getting device related information is complicated and existing packages have some issues with them.
 * This uses bowser internally and exposes a strongly typed interface scoped towards our product.
 */
export class DeviceInformation {
    /** WARNING: may not be known until `updatePlatformSpecificInformation` completes. */
    get vendor() {
        return this._vendor;
    }
    /** WARNING: may not be known until `updatePlatformSpecificInformation` completes. */
    get model() {
        return this._model;
    }
    get architecture() {
        return this._architecture;
    }
    get gpuRenderer() {
        return this._gpuRenderer;
    }
    get deviceModelSubtype() {
        return this._deviceModelSubtype;
    }
    constructor(userAgent, userAgentData, logger) {
        var _a;
        this.userAgent = userAgent;
        this.userAgentData = userAgentData;
        this.logger = logger;
        // Parsed information & properties
        this.browserName = UNKNOWN;
        this.browserVersion = UNKNOWN;
        this.browserMajorVersion = -1;
        this.osName = UNKNOWN;
        this.osVersion = UNKNOWN;
        /** This is value provided by the client user agent hints https://developer.mozilla.org/en-US/docs/Web/API/NavigatorUAData/mobile */
        this.deviceType = UNKNOWN;
        this.engineName = UNKNOWN;
        this.engineVersion = UNKNOWN;
        this.engineMajorVersion = -1;
        this.webviewAppName = UNKNOWN;
        // Properties that may need to be updated asynchronously
        this._vendor = UNKNOWN;
        this._model = UNKNOWN;
        this._architecture = UNKNOWN;
        this._deviceModelSubtype = '';
        this._gpuRenderer = '';
        // Obtain client-side values if necessary
        if (!this.userAgent && isClient) {
            this.userAgent = window.navigator.userAgent;
        }
        if (!this.userAgentData && isClient) {
            this.userAgentData = window.navigator.userAgentData;
        }
        // Parse the user agent if it's defined
        if (this.userAgent) {
            this.parser = getParser(this.userAgent);
            this.browserInformation = this.parser.getResult();
        }
        // Assign device information
        this.supportsTouch = this.getSupportsTouch();
        this.osName = this.getOSName();
        this.osVersion = this.getOSVersion();
        this.browserName = this.getBrowserName();
        this.browserVersion = this.getBrowserVersion(this.osName);
        this.browserMajorVersion = parseInt(this.browserVersion, 10) || -1;
        this._model = this.getModel();
        this.desktopSiteRequested = this.getDesktopSiteRequested();
        this.deviceType = this.getDeviceType();
        this._architecture = this.getArchitecture();
        this._vendor = this.getVendor();
        this.engineName = this.getEngineName();
        this.engineVersion = this.getEngineVersion(this.engineName);
        this.engineMajorVersion = parseInt(this.engineVersion, 10) || -1;
        this.webviewAppName = this.getMobileAppName();
        // Assign shorthand properties
        this.isIOS = this.osName === 'iOS';
        this.isIPadOS = this.osName === 'iPadOS';
        this.isMobile =
            ((_a = this.userAgentData) === null || _a === void 0 ? void 0 : _a.mobile) || this.deviceType === 'mobile';
        this.isTablet = this.deviceType === 'tablet';
        this.isMacOS = this.osName === 'macOS';
        this.isSafari = this.browserName === 'safari';
        this.isEdge = this.browserName === 'edge';
        this.isChrome = this.browserName === 'chrome';
        this.isFireOS = this.osName === 'fireOS';
        this.isAndroid = this.isFireOS || this.osName === 'android';
        this.isTizen = this.osName === 'tizen';
        this.isTV = this.deviceType === 'smarttv';
        this.isSupportedTVBrowser =
            this.isTV && isTvAppUserAgent(this.userAgent);
        this.isVR = this.deviceType === 'vr';
        this.isXbox = this.osName === 'xboxOS';
        this.isWindows = this.osName === 'windows';
        this.isWebKit = this.engineName === 'WebKit';
        this.isLinux = this.osName === 'linux';
        this.isChromeOS = this.osName === 'chromeOS';
        this.isBlinkEngine = this.engineName === 'Blink';
        // Chromium releases M79+ have received ample Edgewater selfhost and test coverage.
        this.isSupportedChromiumBasedBrowser =
            (this.isChrome && this.browserMajorVersion >= 79) ||
                (this.isBlinkEngine && this.engineMajorVersion >= 79);
        // Get Webview device information
        this.isAppleOSWebview = this.getIsAppleOSWebview();
        this.capabilities = new DeviceCapabilities({
            isIOS: this.isIOS,
            isSafari: this.isSafari,
            isXboxOS: this.isXbox,
            isSupportedTVBrowser: this.isSupportedTVBrowser,
            isSupportedChromiumBasedBrowser: this.isSupportedChromiumBasedBrowser,
            parser: this.parser,
            logger: this.logger,
        });
        this.updatePlatformSpecificInformation();
    }
    async updatePlatformSpecificInformation() {
        if (!this.platformSpecificInformationPromise) {
            this.platformSpecificInformationPromise = new Promise((resolve) => {
                (async () => {
                    const tizenInfo = await getTizenSystemInformation();
                    if (tizenInfo) {
                        this._model = `${tizenInfo.model}_${tizenInfo.buildVersion}`;
                        this._vendor = tizenInfo.manufacturer;
                    }
                    resolve();
                })();
            });
        }
        await this.platformSpecificInformationPromise;
    }
    async calculateDeviceModelSubtype() {
        var _a;
        if (this._gpuRenderer && this._deviceModelSubtype) {
            return;
        }
        if (!isClient) {
            return;
        }
        let deobfuscator;
        let matcher;
        try {
            deobfuscator = await import('@gaming/apple-gpu-deobfuscator');
            matcher = await import('@gaming/apple-device-match');
        }
        catch (err) {
            const error = castAsError(err);
            (_a = this.logger) === null || _a === void 0 ? void 0 : _a.error(`Experienced error while importing Apple deobfuscation modules`, error);
        }
        const unmaskRenderer = () => {
            const throwAwayCanvas = document.createElement('canvas');
            const webglContext = throwAwayCanvas.getContext('webgl');
            const debugInfo = webglContext === null || webglContext === void 0 ? void 0 : webglContext.getExtension('WEBGL_debug_renderer_info');
            return debugInfo
                ? webglContext === null || webglContext === void 0 ? void 0 : webglContext.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
                : UNKNOWN;
        };
        const deobfuscateRenderer = () => {
            var _a;
            this._gpuRenderer = unmaskRenderer();
            if (this._gpuRenderer !== 'Apple GPU') {
                return;
            }
            try {
                deobfuscator.getRenderer((result) => {
                    var _a;
                    (_a = this.logger) === null || _a === void 0 ? void 0 : _a.info(`Deobfuscating Apple GPU found matched renderer(s): ${result}`);
                    this._gpuRenderer = result;
                });
            }
            catch (err) {
                const error = castAsError(err);
                (_a = this.logger) === null || _a === void 0 ? void 0 : _a.error(`Determining GPU renderer encountered error`, error);
            }
        };
        const matchDevice = (rendererToMatch) => {
            var _a, _b;
            this._deviceModelSubtype = UNKNOWN;
            if (this.osName !== 'iPadOS' && this.osName !== 'iOS') {
                return;
            }
            try {
                const device = matcher.matchAppleDevice(rendererToMatch);
                if (device) {
                    (_a = this.logger) === null || _a === void 0 ? void 0 : _a.info(`Deobfuscating Apple GPU found matched device(s): ${device}`);
                    this._deviceModelSubtype = device;
                }
            }
            catch (err) {
                const error = castAsError(err);
                (_b = this.logger) === null || _b === void 0 ? void 0 : _b.error(`Matching GPU to Apple device encountered error`, error);
            }
        };
        deobfuscateRenderer();
        matchDevice(this._gpuRenderer);
    }
    getBrowserName() {
        var _a, _b, _c, _d;
        if (this.userAgent) {
            if (googleBotRegex.test(this.userAgent)) {
                return 'googlebot';
            }
            if (bingBotRegex.test(this.userAgent)) {
                return 'bingbot';
            }
            if (this.userAgent.includes('OculusBrowser')) {
                return 'oculus';
            }
        }
        if (((_a = this.userAgentData) === null || _a === void 0 ? void 0 : _a.brands) &&
            this.userAgentData.brands.some((brand) => { var _a; return (_a = brand === null || brand === void 0 ? void 0 : brand.brand) === null || _a === void 0 ? void 0 : _a.includes('Edge'); })) {
            return 'edge';
        }
        const browserName = (_d = (_c = (_b = this.browserInformation) === null || _b === void 0 ? void 0 : _b.browser) === null || _c === void 0 ? void 0 : _c.name) === null || _d === void 0 ? void 0 : _d.toLocaleLowerCase();
        return browserName &&
            Object.keys(Device.BrowserMap).includes(browserName)
            ? Device.BrowserMap[browserName]
            : this.browserName;
    }
    getBrowserVersion(osName) {
        var _a, _b;
        if ((_a = this.userAgentData) === null || _a === void 0 ? void 0 : _a.uaFullVersion) {
            return this.userAgentData.uaFullVersion;
        }
        if (isTvAppUserAgent(this.userAgent) && osName === 'tizen') {
            const tizenBrowserVersion = getTizenAppBrowserVersion(this.userAgent);
            if (tizenBrowserVersion) {
                return tizenBrowserVersion;
            }
        }
        return ((_b = this.browserInformation) === null || _b === void 0 ? void 0 : _b.browser.version) || this.browserVersion;
    }
    getOSName() {
        var _a, _b, _c;
        const isSafari = this.getBrowserName() === 'safari';
        /**
         * Detecting if the user is on an iPad or iPhone is tricky because iPads and iPhones spoof
         * their user agent so they can get desktop sites. We have to check if
         * we have a macos user agent and have a touch enabled device. As of now
         * there is no mac touchscreen devices.
         */
        const isIPadOS = () => {
            var _a, _b, _c;
            if (!isSafari) {
                return false;
            }
            // If it the OS is reported as iPadOS we can trust it. It's when the
            // iPad is pretending to be another OS like iOS or MacOS that we
            // need to check by aspect ratio and touch support.
            if (((_a = this.browserInformation) === null || _a === void 0 ? void 0 : _a.os.name) === 'iPadOS') {
                return true;
            }
            // If the current OS isn't MacOS, iOS then the answer is `false`
            if (!['macOS', 'iOS'].includes((_c = (_b = this.browserInformation) === null || _b === void 0 ? void 0 : _b.os.name) !== null && _c !== void 0 ? _c : '')) {
                return false;
            }
            const aspectRatio = this.getAspectRatio();
            return (this.getSupportsTouch() === Trilean.YES &&
                !isNaN(aspectRatio) &&
                aspectRatio >= IPHONE_IPAD_DIVIDE);
        };
        if (isIPadOS()) {
            return 'iPadOS';
        }
        const isIOS = () => {
            var _a, _b;
            if (!isSafari) {
                return false;
            }
            return (((_a = this.browserInformation) === null || _a === void 0 ? void 0 : _a.os.name) === 'iOS' ||
                (((_b = this.browserInformation) === null || _b === void 0 ? void 0 : _b.os.name) === 'macOS' &&
                    this.getSupportsTouch() === Trilean.YES));
        };
        if (isIOS()) {
            return 'iOS';
        }
        // Example:
        // Mozilla / 5.0(Windows NT 10.0; Win64; x64; Xbox; Xbox One) AppleWebKit / 537.36(KHTML, like Gecko) Chrome / 101.0.4951.41 Safari / 537.36 Edg / 101.0.1210.29
        // Looking to check 'Xbox;' with no letter preceding it.
        const xboxRegex = /(^|[^a-zA-Z])Xbox;/;
        const isXboxOS = () => {
            var _a, _b, _c, _d;
            if (((_a = this.userAgentData) === null || _a === void 0 ? void 0 : _a.model) &&
                !!((_b = this.userAgentData.model) === null || _b === void 0 ? void 0 : _b.match(xboxRegex))) {
                return true;
            }
            if (this.userAgent && this.parser) {
                const isEdgeOnConsoleUserAgent = this.parser.satisfies({ windows: { edge: '>=13' } }) &&
                    this.parser.test(xboxRegex);
                const hasXboxInNavigator = typeof navigator !== 'undefined'
                    ? ((_c = navigator.platform) === null || _c === void 0 ? void 0 : _c.match(xboxRegex)) ||
                        ((_d = this.userAgent) === null || _d === void 0 ? void 0 : _d.match(xboxRegex))
                    : false;
                return !!isEdgeOnConsoleUserAgent || !!hasXboxInNavigator;
            }
            return false;
        };
        if (isXboxOS()) {
            return 'xboxOS';
        }
        // Note: technically "FireOS" applies to more then just FireTV, but since this impacts the streaming config for now we only want
        // to use it for FireTV devices. There is proposed work to create a new streaming config dimension for "form factor", see:
        // Deliverable 38666372: Identify and report android form factor in telemetry
        if (this.userAgent && fireTvUserAgentRegex.test(this.userAgent)) {
            return 'fireOS';
        }
        if (this.userAgentData) {
            const uaDataPlatform = (_a = this.userAgentData.platform) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase();
            if (uaDataPlatform && uaDataPlatform in Device.OSMap) {
                return Device.OSMap[uaDataPlatform];
            }
        }
        const osName = (_c = (_b = this.browserInformation) === null || _b === void 0 ? void 0 : _b.os.name) === null || _c === void 0 ? void 0 : _c.toLocaleLowerCase();
        return osName && Object.keys(Device.OSMap).includes(osName)
            ? Device.OSMap[osName]
            : this.osName;
    }
    getOSVersion() {
        var _a, _b;
        return (((_a = this.userAgentData) === null || _a === void 0 ? void 0 : _a.platformVersion) ||
            ((_b = this.browserInformation) === null || _b === void 0 ? void 0 : _b.os.version) ||
            this.osVersion);
    }
    getEngineName() {
        var _a;
        // Edge on Android is mis-identified by Bowser.JS as EdgeHTML: https://github.com/lancedikson/bowser/issues/416
        // EdgeHTML was NEVER shipped on Android; all Android Edge releases are Chromium (Blink Engine).
        if (this.osName === 'android' && this.browserName === 'edge') {
            return 'Blink';
        }
        return ((_a = this.browserInformation) === null || _a === void 0 ? void 0 : _a.engine.name) || this.engineName;
    }
    getDesktopSiteRequested() {
        var _a;
        if (this.getSupportsTouch() === Trilean.YES &&
            ((_a = this.browserInformation) === null || _a === void 0 ? void 0 : _a.os.name) === 'macOS') {
            return Trilean.YES;
        }
        else {
            return Trilean.NO;
        }
    }
    getVendor() {
        var _a;
        const vendor = ((_a = this.browserInformation) === null || _a === void 0 ? void 0 : _a.platform.vendor) || this.vendor;
        if (vendor === UNKNOWN && this.userAgent) {
            if (this.userAgent.includes(logitechAppId) ||
                // Support Logitech GR-series browser
                logitechGRRegex.test(this.userAgent)) {
                return 'Logitech';
            }
            if (this.userAgent.includes(razerAppId) ||
                // Razer does not properly include their assigned application ID
                // so working around it with a RegEx check
                razerUserAgentRegex.test(this.userAgent)) {
                return 'Razer';
            }
        }
        return vendor;
    }
    getModel() {
        var _a, _b, _c, _d;
        const model = ((_a = this.userAgentData) === null || _a === void 0 ? void 0 : _a.model) ||
            ((_b = this.browserInformation) === null || _b === void 0 ? void 0 : _b.platform.model) ||
            this.model;
        if (model === UNKNOWN && this.userAgent) {
            if (this.userAgent.includes(logitechAppId) ||
                // Support Logitech GR-series browser
                logitechGRRegex.test(this.userAgent)) {
                // eslint-disable-next-line max-len
                // Parse UA, example: Mozilla/5.0 (Linux; Android 11; GR0006 Build/SQ02K.00.0018; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/105.0.5195.136 Mobile Safari/537.36 AgentWeb/5.0.0 UCBrowser/11.6.4.950 36102dd3-6953-45f6-8b48-031fb95e0e0d
                const [, logitechModel] = (_c = /Android\s\d+;\s((?:\w|\d)+)\b/.exec(this.userAgent)) !== null && _c !== void 0 ? _c : [];
                if (logitechModel) {
                    return logitechModel;
                }
                return 'Unknown Logitech Model';
            }
            if (this.userAgent.includes(razerAppId) ||
                // Razer does not properly include their assigned application ID
                // so working around it with a RegEx check
                razerUserAgentRegex.test(this.userAgent)) {
                // eslint-disable-next-line max-len
                // Parse UA, example: Mozilla/5.0 (Linux; Android 12; Razer Edge WiFi Build/SKQ1.211103.001; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/105.0.5195.136 Mobile Safari/537.36
                const [, razerModel] = (_d = /Android\s\d+;\s([0-9a-zA-Z ]+)\sBuild/.exec(this.userAgent)) !== null && _d !== void 0 ? _d : [];
                if (razerModel) {
                    return razerModel;
                }
                return 'Unknown Razer Model';
            }
        }
        switch (this.getOSName()) {
            case 'iPadOS': {
                return 'iPad';
            }
            case 'iOS': {
                return 'iPhone';
            }
        }
        return model;
    }
    getRawDeviceType() {
        var _a;
        return ((_a = this.browserInformation) === null || _a === void 0 ? void 0 : _a.platform.type) &&
            Object.keys(Device.DeviceTypeMap).includes(this.browserInformation.platform.type)
            ? Device.DeviceTypeMap[this.browserInformation.platform.type]
            : this.deviceType;
    }
    getIsVR() {
        if (!this.userAgent) {
            return undefined;
        }
        return (this.userAgent.includes('VR') &&
            this.userAgent.includes('OculusBrowser'));
    }
    getIsTV() {
        const deviceType = this.getRawDeviceType();
        if (deviceType === 'smarttv') {
            return true;
        }
        if (!this.userAgent) {
            return undefined;
        }
        if (isTvUserAgent(this.userAgent)) {
            return true;
        }
        return false;
    }
    getDeviceType() {
        var _a;
        if (this.userAgent) {
            if (this.userAgent.includes(logitechAppId) ||
                this.userAgent.includes(razerAppId) ||
                // Support Logitech GR-series browser
                logitechGRRegex.test(this.userAgent) ||
                // Razer does not properly include their assigned application ID
                // so working around it with a RegEx check
                razerUserAgentRegex.test(this.userAgent)) {
                return 'tablet';
            }
            if (googleBotRegex.test(this.userAgent) ||
                bingBotRegex.test(this.userAgent)) {
                return 'robot';
            }
            if (this.getIsVR()) {
                return 'vr';
            }
        }
        const deviceType = this.getRawDeviceType();
        if (deviceType !== 'smarttv' && this.userAgent) {
            if (isTvUserAgent(this.userAgent)) {
                return 'smarttv';
            }
        }
        const os = this.getOSName();
        if (os === 'iPadOS') {
            return 'tablet';
        }
        if (os === 'iOS') {
            return 'mobile';
        }
        if (deviceType === UNKNOWN && ((_a = this.userAgentData) === null || _a === void 0 ? void 0 : _a.mobile)) {
            return 'mobile';
        }
        return deviceType;
    }
    getArchitecture() {
        var _a;
        let architecture = UNKNOWN;
        if ((_a = this.userAgentData) === null || _a === void 0 ? void 0 : _a.architecture) {
            architecture = this.userAgentData.architecture;
        }
        return architecture;
    }
    getSupportsTouch() {
        // Even if a TV or VR device has touch capabilities those form factors
        // are not appropriate for touch gameplay / UX.
        if (this.getIsVR() || this.getIsTV()) {
            return Trilean.NO;
        }
        // Determine if touch APIs exist on the client
        if (isClient) {
            return 'ontouchstart' in window || navigator.maxTouchPoints > 0
                ? Trilean.YES
                : Trilean.NO;
        }
        return Trilean.UNKNOWN;
    }
    getAspectRatio() {
        if (!isClient) {
            return NaN;
        }
        const heightToWidthRatio = window.screen.height / window.screen.width;
        return heightToWidthRatio > 1
            ? 1 / heightToWidthRatio
            : heightToWidthRatio;
    }
    getEngineVersion(engineName) {
        var _a, _b;
        if (engineName === 'Blink') {
            // For Chromium-based browsers, we should return the Chromium version as the engine version.
            const chromiumVersionMatch = (_a = this.userAgent) === null || _a === void 0 ? void 0 : _a.match(/(?:chrome)\/(\d+(\.?_?\d+)+)/i);
            if (chromiumVersionMatch &&
                chromiumVersionMatch.length > 0 &&
                chromiumVersionMatch[1]) {
                return chromiumVersionMatch[1];
            }
        }
        return ((_b = this.browserInformation) === null || _b === void 0 ? void 0 : _b.engine.version) || this.engineVersion;
    }
    getMobileAppName() {
        var _a, _b, _c, _d;
        if (fbMessengerUserAgentRegex.test((_a = this.userAgent) !== null && _a !== void 0 ? _a : '')) {
            return 'messenger';
        }
        if (fbAppUserAgentRegex.test((_b = this.userAgent) !== null && _b !== void 0 ? _b : '')) {
            return 'fb';
        }
        if (instagramUserAgentRegex.test((_c = this.userAgent) !== null && _c !== void 0 ? _c : '')) {
            return 'instagram';
        }
        if (twitterAppUserAgentRegex.test((_d = this.userAgent) !== null && _d !== void 0 ? _d : '')) {
            return 'twitter';
        }
        return UNKNOWN;
    }
    getIsAppleOSWebview() {
        if ((this.isIOS || this.isIPadOS) && this.webviewAppName !== UNKNOWN) {
            return true;
        }
        return false;
    }
}
//# sourceMappingURL=DeviceInformation.js.map